import {AfterViewInit, Component, ElementRef, Input, OnInit, Renderer2, ViewChild} from '@angular/core';
import {DateControl, firstNestedSymbolControl} from '../../../models/uiArtefakt';
import {ErrorService, ISymbol, MomentService, TranslationService} from '@lv1871/core';
import {UIAusrichtung} from '../../../models/uiAusrichtung';
import {ValidationService} from '../../../services/validation.service';
import {ShellConfigService} from '../../../services/shell-config.service';
import {NgForm, NgModel} from '@angular/forms';
import {UiUpdateService} from '../../../services/ui-update.service';
import {UIDarstellungsart} from '../../../models/uiDarstellungsart';
import {EventService} from '../../../services/event.service';
import {FondsService} from '../../../services/fonds.service';
import {switchMap, tap} from 'rxjs';

class MonthOption {
  name: string;
  index: string;
}

const MONTH_OPTIONS = [
  {name: 'Januar', index: '01'},
  {name: 'Februar', index: '02'},
  {name: 'März', index: '03'},
  {name: 'April', index: '04'},
  {name: 'Mai', index: '05'},
  {name: 'Juni', index: '06'},
  {name: 'Juli', index: '07'},
  {name: 'August', index: '08'},
  {name: 'September', index: '09'},
  {name: 'Oktober', index: '10'},
  {name: 'November', index: '11'},
  {name: 'Dezember', index: '12'}
] as MonthOption[];

@Component({
    selector: 'lv-date-fixed-day',
    templateUrl: './date-fixed-day.component.html',
    styleUrls: ['./date-fixed-day.component.scss'],
    standalone: false
})
export class DateFixedDayComponent implements OnInit, AfterViewInit {

  @ViewChild(NgForm, {static: true})
  private _ngForm: NgForm;
  @ViewChild('monthinputmodel', {static: true})
  private _monthInputmodel: NgModel;
  @ViewChild('yearinputmodel', {static: true})
  private _yearInputmodel: NgModel;

  public monthModel: string;
  public yearModel: string;

  public uiAusrichtung: UIAusrichtung;
  public nestedSymbol: ISymbol;
  public showInfotextCollapsable: boolean = false;
  public hasMonthFocus = false;
  public hasYearFocus = false;
  public showUntruncatedError: boolean = false;
  public isDisabled: boolean;

  private _day: number;
  private _month: number = null;
  private _year: number = null;
  private _uiArtefakt: DateControl;
  private _monthJustBlurred = false;
  private _yearJustBlurred = false;

  constructor(private _momentService: MomentService, private _validationService: ValidationService,
              private _shellConfigService: ShellConfigService, private _uiUpdateService: UiUpdateService,
              private _translationService: TranslationService, private _errorService: ErrorService,
              private _eventService: EventService, private _fondsService: FondsService,
              private _renderer: Renderer2, private _element: ElementRef) {
  }

  get uiArtefakt(): DateControl {
    return this._uiArtefakt;
  }

  @Input() set uiArtefakt(uiArtefakt: DateControl) {
    if (!uiArtefakt.DatumTagVorgegeben || uiArtefakt.DatumTagVorgegeben < 1 || uiArtefakt.DatumTagVorgegeben > 31) {
      this._errorService.pushErrorMessage('Modellierungsfehler: Das Datum-Artefakt ' + uiArtefakt.Identifikator +
          ' mit DatumComponent "TagFixiert" muss ein Attribut "DatumTagVorgegeben" haben im Bereich 1 bis 31.');
      this._day = 1;
    } else {
      this._day = uiArtefakt.DatumTagVorgegeben;
    }
    if (!!uiArtefakt.WertEingabe && !this._uiArtefakt) {
      const newDate = this._momentService.moment(uiArtefakt.WertEingabe, 'YYYY-MM-DD', true)?.toDate();
      this._month = newDate.getMonth() + 1;
      this.monthModel = ('' + this._month).padStart(2, '0');
      this._year = newDate.getFullYear();
      this.yearModel = '' + this._year;
      if (newDate.getDate() !== +this.getFixedDay()) {
        this._errorService.pushWarningMessage('Für das Artefakt ' + uiArtefakt.Identifikator +
            ' wurde ein Datum geladen, dessen Tag (' + newDate.getDate() + ') nicht zum Attribut "DatumTagVorgegeben" passt (' +
            this._day + ').');
      }
    }
    this._uiArtefakt = uiArtefakt;
    this.isDisabled = this._uiArtefakt?.Darstellungsart === UIDarstellungsart.DISABLED;
  }

  public infotextCollapsableChange(event) {
    this.showInfotextCollapsable = event;
  }

  ngAfterViewInit(): void {
    this._renderer.setAttribute(this._element.nativeElement, 'cypress-uuid', this.uiArtefakt.IdentifikatorUuid);
    if(!!this.uiArtefakt.ReferenzIdentifikator) {
      this._renderer.setAttribute(this._element.nativeElement, 'cypress-refId', this.uiArtefakt.ReferenzIdentifikator);
    }
  }

  ngOnInit(): void {
    this.uiAusrichtung = this._shellConfigService.getParameter().uiAusrichtung;
    this.nestedSymbol = firstNestedSymbolControl(this.uiArtefakt.UIArtefakt);
    this._validationService.registerForm(this.uiArtefakt, this._ngForm);
    if (this._uiArtefakt.FeldnameRisikopruefung?.indexOf('versicherungsbeginn') >= 0 &&
        !!this._uiArtefakt.WertEingabe) {
      this._eventService.versicherungsbeginn$.next(this._uiArtefakt.WertEingabe);
      this._fondsService.fetchFondsgruppenUndFonds$.subscribe();
    }
  }

  updateMonth(value: string) {
    this.monthModel = value;
    if (value.match('[0-9]+') && +value >= 1 && +value <= 12) {
      this._month = +value;
    } else {
      this._month = null;
    }
  }

  updateYear(value: string) {
    this.yearModel = value;
    if (value.match('[0-9]+') && +value >= 1871 && +value <= 9999) {
      this._year = +value;
    } else if (value.match('^[0-9]{2}$')) {
      this._year = +this.getOptionsYear().filter(option => option.endsWith('' + this.yearModel))[0];
    } else {
      this._year = null;
    }
  }

  setMonthValue(value: string) {
    this.updateMonth(value);
    this._updateWertEingabe();
  }

  setYearValue(value: string) {
    this.updateYear(value);
    this._updateWertEingabe();
  }

  onMonthBlur() {
    this.hasMonthFocus = false;
    this._monthJustBlurred = true;
    setTimeout(() => {
      this._monthJustBlurred = false;
      if (!this.hasMonthFocus) {
        if (this._month !== null) {
          this.monthModel = ('' + this._month).padStart(2, '0');
        }
        this._updateWertEingabe();
      }
    }, 200);
    if(!!this._uiArtefakt.ReferenzIdentifikator){
      this._eventService.refIdentTouchedOrDirty$.next(this._uiArtefakt.ReferenzIdentifikator);
    }
  }

  onYearBlur() {
    this.hasYearFocus = false;
    this._yearJustBlurred = true;
    setTimeout(() => {
      this._yearJustBlurred = false;
      if (!this.hasYearFocus) {
        if (this._year !== null) {
          this.yearModel = '' + this._year;
        }
        this._updateWertEingabe();
      }
    }, 200);
    if(!!this._uiArtefakt.ReferenzIdentifikator){
      this._eventService.refIdentTouchedOrDirty$.next(this._uiArtefakt.ReferenzIdentifikator);
    }
  }

  getOptionsMonth() {
    if (!this.monthModel) {
      return MONTH_OPTIONS;
    } else {
      return MONTH_OPTIONS.filter(option => option.index.includes(this.monthModel)
          || option.name.toLowerCase().includes(this.monthModel.toLowerCase()));
    }
  }

  getOptionsYear() {
    const currentYear = new Date().getFullYear();
    const options = Array.from({length: currentYear - 1871 + 2}, (_, i) => '' + (currentYear - i + 1));
    if (!this.yearModel) {
      return options;
    } else {
      return options.filter(option => ('' + option).includes(this.yearModel));
    }
  }

  getFixedDay(): string {
    if (this._day <= 9) {
      return '0' + this._day;
    } else if (this._day <= 28 || this._month === null) {
      return '' + this._day;
    } else {
      if ([1, 3, 5, 7, 8, 10, 12].includes(this._month)) {
        return '' + this._day;
      } else if ([4, 6, 9, 11].includes(this._month)) {
        return '' + (this._day > 30 ? 30 : this._day);
      } else {
        if (!this._year || (this._year % 4 !== 0 || (this._year % 100 === 0 && this._year % 400 !== 0))) {
          return '28';
        } else {
          return '' + (this._day > 29 ? 29 : this._day);
        }
      }
    }
  }

  getTotalError() {
    const monthError = this.getMonthError();
    if (!!monthError) {
      return monthError;
    }
    const yearError = this.getYearError();
    if (!!yearError) {
      return yearError;
    }
    const error = this.getError();
    if (!!error) {
      return error;
    }
    return null;
  }

  getError() {
    if (this._uiArtefakt.UIMeldung && this._uiArtefakt.UIMeldung.length
        && this._monthInputmodel.touched && !this._monthJustBlurred && this._yearInputmodel.touched && !this._yearJustBlurred) {
      return this._translationService.translate(this._uiArtefakt.UIMeldung[0].Meldungstext);
    }
    this.showUntruncatedError = false;
    return null;
  }

  getMonthError() {
    if (this._monthInputmodel.errors?.required && this._monthInputmodel.touched && !this._monthJustBlurred) {
      const requiredError = this._translationService.translate('ERRORS.REQUIRED');
      return !!this._uiArtefakt.FehlertextPflichtfeld ?
          this._uiArtefakt.FehlertextPflichtfeld : requiredError;
    }
    if (this._monthInputmodel.errors?.ERROR && this._monthInputmodel.touched && !this.hasMonthFocus) {
      return this._translationService.translate(this._monthInputmodel.errors?.ERROR);
    }
    this.showUntruncatedError = false;
    return null;
  }

  getYearError() {
    if (this._yearInputmodel.errors?.required && this._yearInputmodel.touched && !this._yearJustBlurred) {
      const requiredError = this._translationService.translate('ERRORS.REQUIRED');
      return !!this._uiArtefakt.FehlertextPflichtfeld ?
          this._uiArtefakt.FehlertextPflichtfeld : requiredError;
    }
    if (this._yearInputmodel.errors?.ERROR && this._yearInputmodel.touched && !this.hasYearFocus) {
      return this._translationService.translate(this._yearInputmodel.errors?.ERROR);
    }
    this.showUntruncatedError = false;
    return null;
  }

  public onErrorClick() {
    this.showUntruncatedError = !this.showUntruncatedError;
  }

  private _updateWertEingabe() {
    const newValue = this._computeWertEingabe();
    if (!!newValue && newValue !== this._uiArtefakt.WertEingabe) {
      this._uiArtefakt.WertEingabe = newValue;
      if (this._uiArtefakt.FeldnameRisikopruefung?.indexOf('versicherungsbeginn') >= 0 &&
          !!this._uiArtefakt.WertEingabe) {
        this._eventService.versicherungsbeginn$.next(this._uiArtefakt.WertEingabe);
        this._fondsService.fetchFondsgruppenUndFonds$.pipe(
            tap(_ => this._uiUpdateService.valueChangedSubject$.next(this._uiArtefakt))
        ).subscribe();
      } else {
        this._uiUpdateService.valueChangedSubject$.next(this._uiArtefakt);
      }
    }
  }

  private _computeWertEingabe(): string {
    if (this._month !== null && this._year !== null) {
      return this._year + '-' + ('' + this._month).padStart(2, '0') + '-' + this.getFixedDay();
    }
    return null;
  }
}
